import { AddressInputType } from '@domain/addresses/types/types';
import { captureException } from '@/utils/sentry';
import { getAddressInputFromPlaceResult } from '@domain/addresses/utils/address-transformer';
import { GooglePlaces } from '@domain/addresses/blocs/services/google-places';
import { useCallback, useState } from 'react';

export interface UseAddressDetailsResult {
  /** Callback to fetch full address details for a place ID. Returns AddressInputType on success, null on error. */
  getAddressDetails: (placeId: string) => Promise<AddressInputType | null>;
  /** Boolean indicating if a fetch is in progress. */
  isLoading: boolean;
}

/**
 * Loader to retrieve address details from google places API.
 *
 * @param onError Optional callback invoked when address details fetch fails
 *
 * @returns {@link UseAddressDetailsResult}
 */
export const useGoogleApiAddressDetailsLoader = (
  onError?: (error: unknown) => void,
): UseAddressDetailsResult => {
  const [isLoading, setIsLoading] = useState(false);

  const getAddressDetails: (
    placeId: string,
  ) => Promise<AddressInputType | null> = useCallback(
    async (placeId) => {
      setIsLoading(true);
      try {
        const placeResult = await GooglePlaces.getPlaceDetails(placeId);
        return getAddressInputFromPlaceResult(placeResult);
      } catch (error) {
        captureException(error);
        onError?.(error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [onError],
  );

  return {
    getAddressDetails,
    isLoading,
  };
};
